<template>
    <div :class="{
        'day-picker':   dayPicker,
    }" class="picker">
        <div class="header">
            <button v-if="dayPicker"
                    class="no-border iconed slider"
                    @click="prevMonth()">
                <IconChevron class="icon left" />
            </button>
            <div class="title">
                <div :class="{ active: monthPicker }"
                     class="month"
                     @click="$emit('changePicker', 'month')"
                >{{ months[month] }}
                </div>
                <div :class="{ active: yearPicker }"
                     class="year"
                     @click="$emit('changePicker', 'year')"
                >{{ year }}
                </div>
            </div>
            <button v-if="dayPicker"
                    class="no-border iconed slider"
                    @click="nextMonth()">
                <IconChevron class="icon right" />
            </button>
        </div>
        <div class="middle">

            <DayPicker v-show="dayPicker"
                       v-model="selected"
                       :dateDisabled="dateDisabled"
                       :dualPicker="dualPicker"
                       :hoverDate="hoverDate"
                       :hoverForward="hoverForward"
                       :maxDate="maxDate"
                       :minDate="minDate"
                       :month="month"
                       :selectRange="selectRange"
                       :year="year"
                       class="day-picker"
                       @hoverDate="$emit('hoverDate', $event)"
            />

            <MonthPicker v-show="monthPicker"
                         v-model="month"
                         :maxDate="maxDate"
                         :minDate="minDate"
                         :year="year"
                         class="month-picker"
                         @input="$emit('changePicker', 'day')"
            />

            <YearPicker v-show="yearPicker"
                        v-model="year"
                        :maxDate="maxDate"
                        :minDate="minDate"
                        @input="$emit('changePicker', 'day')"
            />
        </div>
    </div>
</template>

<script>
import { changeMonth, findLastIndex, MONTHS_OF_YEAR } from '../../util.js';
import { validateScalarDateValue, validateVectorDateValue } from '../../validation.js';
import IconChevron from '../icons/chevron.svg?component';
import DayPicker from './DayPicker.vue';
import MonthPicker from './MonthPicker.vue';
import YearPicker from './YearPicker.vue';


export default {
    props: {
        value: {
            // Date | Array<Date>
        },
        picker: {
            type: String,
            required: true,
        },
        dateDisabled: {
            type: Function,
            required: true,
        },
        minDate: {
            type: Date,
            required: true,
        },
        maxDate: {
            type: Date,
            required: true,
        },
        selectRange: {
            type: Boolean,
            required: true,
        },
        timePicker: {
            type: Boolean,
            required: true,
        },
        dualPicker: {
            type: Boolean,
            required: true,
        },
        pickerIndex: {
            type: Number,
            required: true,
        },
        hoverDate: {
            type: Date,
        },
        hoverForward: {
            type: Boolean,
        },
    },
    components: {
        IconChevron,
        DayPicker,
        MonthPicker,
        YearPicker,
    },
    computed: {
        dayPicker() {
            return this.picker === 'day';
        },
        monthPicker() {
            return this.picker === 'month';
        },
        yearPicker() {
            return this.picker === 'year';
        },
        months() {
            return MONTHS_OF_YEAR;
        },
        month: {
            get() {
                if (this.date == null)
                    return 0;

                return this.date.getMonth();
            },
            set(value) {
                this.date = new Date(this.year, value);
            },
        },
        year: {
            get() {
                if (this.date == null)
                    return 0;

                return this.date.getFullYear();
            },
            set(value) {
                this.date = new Date(value, this.month);
            },
        },
        selected: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
    },
    data() {
        return {
            date: null,
        };
    },
    created() {
        if (this.selectRange) {
            validateVectorDateValue(this.value);
        } else {
            validateScalarDateValue(this.value);
        }

        this.date = this.getPickerDate();
    },
    watch: {
        value() {
            this.date = this.getPickerDate();
        },
    },
    methods: {
        prevMonth() {
            this.date = changeMonth(this.date, -1);
        },
        nextMonth() {
            this.date = changeMonth(this.date, +1);
        },
        getPickerDate() {
            const now = new Date();

            if (this.value == null)
                return changeMonth(now, this.pickerIndex);

            if (!Array.isArray(this.value))
                return this.value;

            if (this.dualPicker)
                return this.value[this.pickerIndex] ?? now;

            const index = findLastIndex(this.value, it => it != null);
            if (index < 0)
                return now;

            return this.value[index];
        },
    },
};
</script>

<style lang="scss" scoped>
.picker {

    &:not(.day-picker) {
        & > .header {
            justify-content: center;
        }
    }

    & > .header {
        display: flex;
        justify-content: space-between;
        padding: 8px 8px 0 8px;

        .icon {
            margin-right: 0;

            &.right {
                transform: rotate(180deg);
            }
        }

        .title {
            display: inline-flex;
            flex: none;
        }

        .month,
        .year {
            font-size: 16px;
            line-height: 22px;
            padding: 6px 8px;
            border-radius: 4px;
            cursor: pointer;

            &.active,
            &:hover {
                background: rgba(16, 24, 40, 0.1);
                color: var(--text-color);
            }
        }

        .month {
            color: var(--text-color);
            font-weight: 500;
        }

        .year {
            color: rgba(16, 24, 40, 0.5);
        }

        .slider:hover {
            background: rgba(16, 24, 40, 0.05) !important;
        }
    }

    .middle {
        display: flex;
        flex-direction: column;
        padding: 12px;

        .month-picker {
            padding: 14px 0;
        }
    }

}
</style>
